import { inject, Injectable } from '@angular/core';
import {
  BathroomConfigurationState,
  ValidationError,
  ValidationResult,
  ValidationRule,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import {
  COMMON_RULES,
  KENTIA_RULES,
  KENTIA_RANGE_ID,
  createKentiaRules,
  extractKentiaConstraints,
  KentiaConstraints,
} from './validation-rules';
import { BathroomService } from '../../applications/bathroom.service';

@Injectable({
  providedIn: 'root',
})
export class BathroomValidationService {
  private readonly bathroomService = inject(BathroomService);
  private readonly commonRules: ValidationRule[] = COMMON_RULES;
  private readonly rangeRules: Map<string, ValidationRule[]> = new Map();
  private readonly rangeConstraints: Map<string, KentiaConstraints> = new Map();
  private constraintsLoaded = false;

  constructor() {
    // Enregistrer les règles par défaut par gamme
    this.registerRangeRules(KENTIA_RANGE_ID, KENTIA_RULES);
  }

  /**
   * Charge les contraintes depuis le service pour une gamme donnée et met à jour les règles
   * @param rangeId L'identifiant de la gamme
   */
  async loadConstraintsForRange(rangeId: string): Promise<void> {
    try {
      const constraints = await this.bathroomService.getConstraintsByRangeId(rangeId);

      const kentiaConstraints = extractKentiaConstraints(constraints);
      this.rangeConstraints.set(rangeId, kentiaConstraints);
      const rules = createKentiaRules(kentiaConstraints);
      this.registerRangeRules(rangeId, rules);

      this.constraintsLoaded = true;
    } catch (error) {
      console.error(`Erreur lors du chargement des contraintes pour la gamme ${rangeId}:`, error);
    }
  }

  /**
   * Récupère les contraintes chargées pour une gamme
   * @param rangeId L'identifiant de la gamme
   */
  getConstraintsForRange(rangeId: string): KentiaConstraints | undefined {
    return this.rangeConstraints.get(rangeId);
  }

  /**
   * Vérifie si les contraintes ont été chargées
   */
  areConstraintsLoaded(): boolean {
    return this.constraintsLoaded;
  }

  /**
   * Enregistre des règles pour une gamme spécifique
   */
  registerRangeRules(rangeId: string, rules: ValidationRule[]): void {
    this.rangeRules.set(rangeId, rules);
  }

  /**
   * Récupère toutes les règles applicables pour une gamme
   * Combine les règles communes + les règles spécifiques à la gamme
   */
  getRulesForRange(rangeId: string): ValidationRule[] {
    const rangeSpecificRules = this.rangeRules.get(rangeId) || [];
    return [...this.commonRules, ...rangeSpecificRules];
  }

  /**
   * Récupère uniquement les règles communes (applicables à toutes les gammes)
   */
  getCommonRules(): ValidationRule[] {
    return [...this.commonRules];
  }

  /**
   * Récupère uniquement les règles spécifiques à une gamme
   */
  getRangeSpecificRules(rangeId: string): ValidationRule[] {
    return [...(this.rangeRules.get(rangeId) || [])];
  }

  /**
   * Valide toute la configuration et retourne les erreurs
   * @param state L'état de la configuration à valider
   * @param rangeId L'identifiant de la gamme (optionnel, utilise toutes les règles si non fourni)
   */
  validateConfiguration(state: BathroomConfigurationState, rangeId?: string): ValidationError[] {
    const allErrors: ValidationError[] = [];
    const rules = rangeId ? this.getRulesForRange(rangeId) : this.getAllRules();

    rules.forEach(rule => {
      const result = rule.validate(state);
      if (!result.valid) {
        allErrors.push(...result.errors);
      }
    });

    if (allErrors.length > 0) {
      console.warn('Validation errors:', allErrors);
    }

    return allErrors;
  }

  /**
   * Valide une règle spécifique
   */
  validateRule(ruleId: string, state: BathroomConfigurationState, rangeId?: string): ValidationResult {
    const rules = rangeId ? this.getRulesForRange(rangeId) : this.getAllRules();
    const rule = rules.find(r => r.id === ruleId);

    if (!rule) {
      return { valid: true, errors: [] };
    }

    return rule.validate(state);
  }

  /**
   * Vérifie si un changement est valide avant de l'appliquer
   */
  canApplyChange(
    state: BathroomConfigurationState,
    change: Partial<BathroomConfigurationState>,
    rangeId?: string
  ): { valid: boolean; errors: ValidationError[] } {
    const testState = { ...state, ...change };
    const errors = this.validateConfiguration(testState, rangeId);

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Récupère les règles applicables pour un champ donné
   */
  getRulesForField(fieldName: string, rangeId?: string): ValidationRule[] {
    const rules = rangeId ? this.getRulesForRange(rangeId) : this.getAllRules();
    return rules.filter(rule => {
      return rule.description.toLowerCase().includes(fieldName.toLowerCase());
    });
  }

  /**
   * Ajoute une règle personnalisée (commune ou spécifique à une gamme)
   */
  addRule(rule: ValidationRule): void {
    if (rule.rangeId) {
      const existingRules = this.rangeRules.get(rule.rangeId) || [];
      existingRules.push(rule);
      this.rangeRules.set(rule.rangeId, existingRules);
    } else {
      (this.commonRules as ValidationRule[]).push(rule);
    }
  }

  /**
   * Supprime une règle par son ID
   */
  removeRule(ruleId: string): void {
    // Chercher dans les règles communes
    const commonIndex = this.commonRules.findIndex(r => r.id === ruleId);
    if (commonIndex !== -1) {
      (this.commonRules as ValidationRule[]).splice(commonIndex, 1);
      return;
    }

    // Chercher dans les règles par gamme
    for (const [rangeId, rules] of this.rangeRules.entries()) {
      const index = rules.findIndex(r => r.id === ruleId);
      if (index !== -1) {
        rules.splice(index, 1);
        this.rangeRules.set(rangeId, rules);
        return;
      }
    }
  }

  /**
   * Récupère toutes les règles (communes + toutes les gammes)
   */
  getAllRules(): ValidationRule[] {
    const allRangeRules: ValidationRule[] = [];
    for (const rules of this.rangeRules.values()) {
      allRangeRules.push(...rules);
    }
    return [...this.commonRules, ...allRangeRules];
  }

  /**
   * Récupère la liste des gammes enregistrées
   */
  getRegisteredRanges(): string[] {
    return Array.from(this.rangeRules.keys());
  }
}
